import { useRef, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import type { SystemLog } from '../../types';

export interface LogsCopyButtonProps {
  selectedLogs: SystemLog[];
  onCopied: () => void;
}

async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall back below.
    }
  }

  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);
    return copied;
  } catch {
    return false;
  }
}

export function LogsCopyButton({ selectedLogs, onCopied }: LogsCopyButtonProps) {
  const { addToast } = useToast();
  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef<number | null>(null);

  const handleCopy = async () => {
    const formattedJson = JSON.stringify(selectedLogs, null, 2);
    const didCopy = await copyToClipboard(formattedJson);

    if (!didCopy) {
      addToast('Unable to copy selected logs to clipboard.', 'error');
      return;
    }

    setCopied(true);
    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current);
    }

    resetTimerRef.current = window.setTimeout(() => {
      setCopied(false);
    }, 1200);

    addToast(`${selectedLogs.length} selected log(s) copied.`, 'success');
    onCopied();
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={selectedLogs.length === 0}
      className="rounded-md bg-primary-700 px-3 py-2 text-sm font-medium text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60 focus:ring-2 focus:ring-primary-500 focus:outline-none"
      aria-label={`Copy ${selectedLogs.length} selected logs`}
    >
      {copied ? 'Copied!' : 'Copy Selected'}
    </button>
  );
}
